import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HTTPService } from 'src/app/services/http.service';
import { UserAuthenticationServices } from 'src/app/services/user-authentication.services';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { ScreenTemplate } from '../interfaces/template.models';

@Injectable({
  providedIn: 'root'
})
export class TemplateApiService {
  
  constructor(
    private http: HTTPService,
    private uas: UserAuthenticationServices,
    private snackbar: SnackbarService
  ) {}

  /**
   * Lista todos os templates da empresa (incluindo públicos)
   */
  getTemplates(category?: string, search?: string, includePublic: boolean = true): Observable<ScreenTemplate[]> {
    const codecompany = this.uas.user.codecompany;

    let condition = `ISDELETED = 0 AND ISACTIVE = 1`;

    if (includePublic) {
      condition += ` AND (CODECOMPANY = ${codecompany} OR ISPUBLIC = 1)`;
    } else {
      condition += ` AND CODECOMPANY = ${codecompany}`;
    }

    if (category && category !== 'all') {
      condition += ` AND CATEGORY = '${category}'`;
    }

    if (search && search.trim()) {
      const searchTerm = search.trim().replace(/'/g, "''"); // Escape single quotes
      condition += ` AND (NAME LIKE '%${searchTerm}%' OR DESCRIPTION LIKE '%${searchTerm}%')`;
    }

    const ob = {
      entityName: 'Mktpscreentemplate',
      sql: `SELECT * FROM MKTPSCREENTEMPLATE WHERE ${condition} ORDER BY CREATEDON DESC`
    };

    return this.http.post(ob, 'JsonBrokerServices/findnative', false, true, true).pipe(
      map((response: any) => {
        if (response && response.SQL) {
          return response.SQL.map((item: any) => this.mapFromApi(item));
        }
        return [];
      }),
      catchError((error) => {
        console.error('Erro ao carregar templates:', error);
        this.snackbar.showError('Erro ao carregar templates');
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtém template por ID
   */
  getTemplateById(idTemplate: number): Observable<ScreenTemplate> {
    const ob = {
      entityName: 'Mktpscreentemplate',
      sql: `SELECT * FROM MKTPSCREENTEMPLATE WHERE IDTEMPLATE = ${idTemplate} AND ISDELETED = 0`
    };

    return this.http.post(ob, 'JsonBrokerServices/findnative', false, true, true).pipe(
      map((response: any) => {
        if (response && response.SQL && response.SQL.length > 0) {
          return this.mapFromApi(response.SQL[0]);
        }
        throw new Error('Template não encontrado');
      }),
      catchError((error) => {
        console.error('Erro ao carregar template:', error);
        this.snackbar.showError('Erro ao carregar template');
        return throwError(() => error);
      })
    );
  }

  /**
   * Cria novo template
   * ✅ CORRIGIDO: Agora salva JSON completo (tabs/panels/fields/rows) ao invés de apenas components
   */
  createTemplate(template: ScreenTemplate, isPublic: boolean = false): Observable<number> {
    const codecompany = this.uas.user.codecompany;
    const codecontract = this.uas.user.companylist && this.uas.user.companylist.length > 0 
      ? this.uas.user.companylist[0].codecontract 
      : null;
    const iduser = this.uas.user.iduser;
    const username = this.uas.user.username;

    // ✅ Salvar JSON completo: tabs/panels/fields/rows OU components (compatibilidade)
    let jsonToSave: any;
    if ((template as any).tabs && (template as any).panels && (template as any).fields) {
      jsonToSave = {
        tabs: (template as any).tabs,
        panels: (template as any).panels,
        fields: (template as any).fields,
        rows: (template as any).rows || [] // Incluir array de Rows
      };
      // Barra de Contexto: incluir contextFields se existir
      if ((template as any).contextFields && (template as any).contextFields.length > 0) {
        jsonToSave.contextFields = (template as any).contextFields;
      }
    } else {
      jsonToSave = {
        components: template.components,
        gridConfig: template.gridConfig
      };
    }
    
    // Contar componentes: usar fields se existir, senão components
    const componentCount = (template as any).fields 
      ? (template as any).fields.length 
      : template.components.length;

    const ob = {
      entityName: 'Mktpscreentemplate',
      TEMPLATEKEY: this.generateTemplateKey(template.name),
      NAME: template.name,
      DESCRIPTION: template.description,
      CATEGORY: template.category,
      CODECOMPANY: codecompany,
      CODECONTRACT: codecontract,
      ISPUBLIC: isPublic ? 1 : 0,
      TEMPLATEJSON: JSON.stringify(jsonToSave),
      GRIDCOLUMNS: template.gridConfig.columns,
      GRIDROWS: template.gridConfig.rows,
      GRIDGAP: template.gridConfig.gap || '16px',
      COMPONENTCOUNT: componentCount,
      VERSION: 1,
      ISACTIVE: 1,
      ISPUBLISHED: 0,
      IDUSER: iduser,
      CREATEDBY: username
    };

    return this.http.post(ob, 'JsonBrokerServices/save', true, true, true).pipe(
      map((response: any) => {
        if (response && response.IDTEMPLATE) {
          this.snackbar.showSuccess('Template criado com sucesso!');
          return response.IDTEMPLATE;
        }
        throw new Error('Erro ao criar template');
      }),
      catchError((error) => {
        console.error('Erro ao criar template:', error);
        this.snackbar.showError('Erro ao criar template');
        return throwError(() => error);
      })
    );
  }

  /**
   * Atualiza template existente
   * ✅ CORRIGIDO: Agora salva JSON completo (tabs/panels/fields/rows) ao invés de apenas components
   */
  updateTemplate(idTemplate: number, template: ScreenTemplate): Observable<boolean> {
    const username = this.uas.user.username;
    const codecompany = this.uas.user.codecompany;
    const codecontract = this.uas.user.companylist && this.uas.user.companylist.length > 0 
      ? this.uas.user.companylist[0].codecontract 
      : null;

    // ✅ Salvar JSON completo: tabs/panels/fields/rows OU components (compatibilidade)
    let jsonToSave: any;
    if ((template as any).tabs && (template as any).panels && (template as any).fields) {
      jsonToSave = {
        tabs: (template as any).tabs,
        panels: (template as any).panels,
        fields: (template as any).fields,
        rows: (template as any).rows || [] // Incluir array de Rows
      };
      // Barra de Contexto: incluir contextFields se existir
      if ((template as any).contextFields && (template as any).contextFields.length > 0) {
        jsonToSave.contextFields = (template as any).contextFields;
      }
    } else {
      jsonToSave = {
        components: template.components,
        gridConfig: template.gridConfig
      };
    }
    
    // Contar componentes: usar fields se existir, senão components
    const componentCount = (template as any).fields 
      ? (template as any).fields.length 
      : template.components.length;

    const ob = {
      entityName: 'Mktpscreentemplate',
      IDTEMPLATE: idTemplate,
      CODECOMPANY: codecompany,
      CODECONTRACT: codecontract,
      NAME: template.name,
      DESCRIPTION: template.description,
      CATEGORY: template.category,
      TEMPLATEJSON: JSON.stringify(jsonToSave),
      GRIDCOLUMNS: template.gridConfig.columns,
      GRIDROWS: template.gridConfig.rows,
      GRIDGAP: template.gridConfig.gap || '16px',
      COMPONENTCOUNT: componentCount,
      MODIFIEDBY: username,
      //MODIFIEDON: new Date()
    };

    return this.http.post(ob, 'JsonBrokerServices/save', true, true, true).pipe(
      map(() => {
        this.snackbar.showSuccess('Template atualizado com sucesso!');
        return true;
      }),
      catchError((error) => {
        console.error('Erro ao atualizar template:', error);
        this.snackbar.showError('Erro ao atualizar template');
        return throwError(() => error);
      })
    );
  }

  /**
   * Exclui template (soft delete)
   */
  deleteTemplate(idTemplate: number): Observable<boolean> {
    const username = this.uas.user.username;
    const codecompany = this.uas.user.codecompany;
    const codecontract = this.uas.user.companylist && this.uas.user.companylist.length > 0 
      ? this.uas.user.companylist[0].codecontract 
      : null;

    const ob = {
      entityName: 'Mktpscreentemplate',
      IDTEMPLATE: idTemplate,
      CODECOMPANY: codecompany,
      CODECONTRACT: codecontract,
      ISDELETED: 1,
      DELETEDBY: username,
      DELETEDON: new Date()
    };

    return this.http.post(ob, 'JsonBrokerServices/save', true, true, true).pipe(
      map(() => {
        this.snackbar.showSuccess('Template excluído com sucesso!');
        return true;
      }),
      catchError((error) => {
        console.error('Erro ao excluir template:', error);
        this.snackbar.showError('Erro ao excluir template');
        return throwError(() => error);
      })
    );
  }

  /**
   * Duplica template
   */
  duplicateTemplate(idTemplate: number, newName: string): Observable<number> {
    return new Observable((observer) => {
      this.getTemplateById(idTemplate).subscribe({
        next: (template) => {
          const duplicated: ScreenTemplate = {
            ...template,
            id: this.generateTemplateKey(newName),
            name: newName,
            description: `${template.description} (Cópia)`
          };

          this.createTemplate(duplicated, false).subscribe({
            next: (newId) => {
              observer.next(newId);
              observer.complete();
            },
            error: (err) => observer.error(err)
          });
        },
        error: (err) => observer.error(err)
      });
    });
  }

  /**
   * Publica template
   */
  publishTemplate(idTemplate: number): Observable<boolean> {
    const codecompany = this.uas.user.codecompany;
    const codecontract = this.uas.user.companylist && this.uas.user.companylist.length > 0 
      ? this.uas.user.companylist[0].codecontract 
      : null;

    const ob = {
      entityName: 'Mktpscreentemplate',
      IDTEMPLATE: idTemplate,
      CODECOMPANY: codecompany,
      CODECONTRACT: codecontract,
      ISPUBLISHED: 1,
      MODIFIEDON: new Date()
    };

    return this.http.post(ob, 'JsonBrokerServices/save', true, true, true).pipe(
      map(() => {
        this.snackbar.showSuccess('Template publicado com sucesso!');
        return true;
      }),
      catchError((error) => {
        console.error('Erro ao publicar template:', error);
        this.snackbar.showError('Erro ao publicar template');
        return throwError(() => error);
      })
    );
  }

  /**
   * Converte de API para modelo interno
   * ✅ CORRIGIDO: Agora carrega JSON completo (tabs/panels/fields) se existir
   */
  private mapFromApi(apiTemplate: any): ScreenTemplate {
    let parsedJson;

    try {
      parsedJson = JSON.parse(apiTemplate.TEMPLATEJSON);
    } catch (e) {
      console.error('Erro ao fazer parse do JSON do template:', e);
      parsedJson = { components: [], gridConfig: { columns: 24, rows: 15, gap: '16px' } };
    }

    // Criar objeto base do template
    const baseTemplate: any = {
      id: apiTemplate.TEMPLATEKEY,
      idTemplate: apiTemplate.IDTEMPLATE, // ID numérico do banco
      name: apiTemplate.NAME,
      description: apiTemplate.DESCRIPTION,
      category: apiTemplate.CATEGORY,
      createdAt: apiTemplate.CREATEDON ? new Date(apiTemplate.CREATEDON) : new Date(),
      updatedAt: apiTemplate.MODIFIEDON ? new Date(apiTemplate.MODIFIEDON) : undefined
    };

    // Se o JSON tem estrutura completa (tabs/panels/fields), usar ela
    if (parsedJson.tabs && parsedJson.panels && parsedJson.fields) {
      const result: any = {
        ...baseTemplate,
        tabs: parsedJson.tabs,
        panels: parsedJson.panels,
        fields: parsedJson.fields,
        rows: parsedJson.rows || [], // Carregar array de Rows (ou vazio se não existir)
        // Manter gridConfig e components vazios para compatibilidade
        components: [],
        gridConfig: {
          columns: apiTemplate.GRIDCOLUMNS || 24,
          rows: apiTemplate.GRIDROWS || 15,
          gap: apiTemplate.GRIDGAP || '16px'
        }
      };
      // Barra de Contexto: restaurar contextFields do JSON salvo
      if (parsedJson.contextFields && parsedJson.contextFields.length > 0) {
        result.contextFields = parsedJson.contextFields;
      }
      return result;
    }

    // Formato antigo: usar components/gridConfig
    return {
      ...baseTemplate,
      components: parsedJson.components || [],
      gridConfig: {
        columns: apiTemplate.GRIDCOLUMNS || 24,
        rows: apiTemplate.GRIDROWS || 15,
        gap: apiTemplate.GRIDGAP || '16px'
      }
    };
  }

  /**
   * Gera chave única para template
   */
  private generateTemplateKey(name: string): string {
    const normalized = name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^a-z0-9]/g, '-') // Substitui caracteres especiais por hífen
      .replace(/-+/g, '-') // Remove hífens duplicados
      .replace(/^-|-$/g, ''); // Remove hífens do início e fim

    const timestamp = Date.now();
    return `${normalized}-${timestamp}`;
  }
}

